// Catalog Shell - Step Progress Indicator
// ============================================================================

import { createElement, clearElement } from '../../utils/dom';
import { icons } from '../../utils/icons';

// ============================================================================
// Types
// ============================================================================

export type StepStatus = 'pending' | 'active' | 'completed' | 'error';

export interface StepProgressConfig {
    /** Step labels in order */
    steps: string[];
    /** Current step index (0-based). Default: 0 */
    currentStep?: number;
    /** Compact mode: circles only, no labels. Default: false */
    compact?: boolean;
}

export interface StepProgress {
    /** The root DOM element */
    element: HTMLElement;
    /** Set the current step (marks previous steps as completed) */
    setCurrentStep(index: number): void;
    /** Mark a specific step with a status */
    setStepStatus(index: number, status: StepStatus): void;
    /** Reset all steps to pending */
    reset(): void;
}

// ============================================================================
// Factory
// ============================================================================

export function createStepProgress(config: StepProgressConfig): StepProgress {
    const { steps, currentStep = 0, compact = false } = config;

    const statuses: StepStatus[] = steps.map((_, i) => {
        if (i < currentStep) return 'completed';
        if (i === currentStep) return 'active';
        return 'pending';
    });

    const containerClasses = ['cs-step-progress'];
    if (compact) containerClasses.push('cs-step-progress--compact');

    const container = createElement('div', {
        className: containerClasses.join(' '),
    });

    const stepElements: HTMLElement[] = [];

    // Build step elements
    for (let i = 0; i < steps.length; i++) {
        const stepEl = createElement('div', {
            className: `cs-step-progress-step cs-step-progress-step--${statuses[i]}`,
        });

        const number = createElement('div', { className: 'cs-step-progress-number' });
        renderNumberContent(number, i, statuses[i]);
        stepEl.appendChild(number);

        if (!compact) {
            const label = createElement('div', {
                className: 'cs-step-progress-label',
                textContent: steps[i],
            });
            stepEl.appendChild(label);
        }

        stepElements.push(stepEl);
        container.appendChild(stepEl);
    }

    // API
    function updateStep(index: number) {
        if (index < 0 || index >= steps.length) return;
        const el = stepElements[index];
        const status = statuses[index];

        // Update CSS class
        el.className = `cs-step-progress-step cs-step-progress-step--${status}`;

        // Update number content
        const numberEl = el.querySelector('.cs-step-progress-number');
        if (numberEl) {
            renderNumberContent(numberEl as HTMLElement, index, status);
        }
    }

    return {
        element: container,

        setCurrentStep(index: number) {
            for (let i = 0; i < steps.length; i++) {
                if (i < index) statuses[i] = 'completed';
                else if (i === index) statuses[i] = 'active';
                else statuses[i] = 'pending';
                updateStep(i);
            }
        },

        setStepStatus(index: number, status: StepStatus) {
            if (index < 0 || index >= steps.length) return;
            statuses[index] = status;
            updateStep(index);
        },

        reset() {
            for (let i = 0; i < steps.length; i++) {
                statuses[i] = i === 0 ? 'active' : 'pending';
                updateStep(i);
            }
        },
    };
}

function renderNumberContent(el: HTMLElement, index: number, status: StepStatus): void {
    clearElement(el);
    switch (status) {
        case 'completed':
            el.appendChild(icons.check());
            break;
        case 'error':
            el.textContent = '!';
            break;
        default:
            el.textContent = String(index + 1);
    }
}
